import { useState } from 'react';

import { useBookingPaymentAccounts, type Booking, type PaymentKind } from '@/api/bookings';
import { enterKeyScope, useAutoFocusFirstField } from '@/components/enterKeyNavigation';
import { Field, textAreaClass } from '@/components/Field';
import { Button, Card, Notice } from '@/components/PageHeading';
import { QuickAmounts, SplitBoxes } from '@/components/SplitBoxes';
import { useSplitParts } from '@/components/useSplitParts';
import { formatAmount } from '@core/format/money';
// Shared with the phone's payment screen: which kinds a person may enter, and what is outstanding.
import { offerableKinds, suggestedFor, takePartsLabel } from '@core/bookings/payment';
import { paymentKindLabel, paymentMethodLabel } from '@core/bookings/paymentWords';
import {
  fillOne,
  filledParts,
  splitProblem,
  splitTotal,
  splitWords,
  withoutSaved,
  type PaidInParts,
  type SplitPart,
} from '@core/cash/split';
import { humanizeEnum } from '@core/format/text';

import { CounterBand } from './CounterBand';
import { Choice, Group } from './Dialog';
import { MoneyLine, MoneyLines } from './MoneyLine';

const methodWord = (method: SplitPart['method']) =>
  method === 'Cash' ? 'cash' : paymentMethodLabel[method];

/**
 * Taking a payment against a booking — a full page, the phone's `app/booking/pay.tsx` on the
 * website. **The sale counter's layout** (owner, 2026-11-01, canvas "Money in, by account"): what
 * the money is for on the left — the kinds with what is left on each, and the booking's figures —
 * and the Payment card on the right, **a box per account**. ₹3,000 into the till and ₹2,040 by UPI
 * into the bank is two receipts, each into its own account; what is outstanding starts in the
 * till's box. The kinds and figures are the core's (`bookings/payment.ts`), the boxes `cash/split.ts`.
 */
export function PaymentPage({
  booking,
  branchId,
  currency = '',
  busy = false,
  initialKind = 'RentalBalance',
  initialAmount,
  onClose,
  onRecord,
}: {
  booking: Booking;
  branchId: number | undefined;
  currency?: string;
  busy?: boolean;
  initialKind?: PaymentKind;
  /** What the opener knows is owed for `initialKind` that the booking does not say yet. */
  initialAmount?: number | undefined;
  onClose: () => void;
  /** Sends the parts; a refusal part-way answers what was saved, and the page says so. */
  onRecord: (
    kind: PaymentKind,
    notes: string | null,
    parts: readonly SplitPart[],
  ) => Promise<PaidInParts<Booking>>;
}) {
  const suggestionFor = (option: PaymentKind) =>
    option === initialKind && initialAmount !== undefined
      ? initialAmount
      : suggestedFor(booking, option);
  const [kind, setKind] = useState<PaymentKind>(initialKind);
  const [notes, setNotes] = useState('');
  const [tried, setTried] = useState(false);
  const [partly, setPartly] = useState<string | null>(null);
  const accounts = useBookingPaymentAccounts(branchId);
  const fields = useAutoFocusFirstField<HTMLDivElement>();

  const suggested = suggestionFor(kind);
  // Untouched, what is outstanding sits in the first box (the till); typing takes over.
  const { parts, change, reset } = useSplitParts(accounts.data, (fresh) =>
    suggested > 0 ? fillOne(fresh, suggested) : fresh,
  );
  const total = splitTotal(parts);
  const used = filledParts(parts).length;
  const problem = splitProblem(parts, 'into');
  const givingBack = kind === 'DepositRefund' || kind === 'RentalRefund';
  const cash = (figure: number | null) => `${currency}${formatAmount(figure)}`;

  const record = () => {
    setTried(true);
    setPartly(null);

    if (problem || busy) {
      return;
    }

    void onRecord(kind, notes.trim() || null, parts)
      .then(({ saved, failed }) => {
        if (!failed) {
          return;
        }

        // What went through is emptied, so pressing Take again sends only what is left.
        change((current) => withoutSaved(current, saved));
        const words = splitWords(
          saved.map((each) => each.part),
          methodWord,
          cash,
          givingBack ? 'from' : 'into',
        );
        setPartly(`${words} — done. ${failed.part.accountName} was not.`);
      })
      // The refusal is already on the toast; nothing was saved.
      .catch(() => undefined);
  };

  return (
    <div {...enterKeyScope}>
      <CounterBand
        eyebrow={`${booking.documentNumber || booking.quotationNumber || 'Unnumbered booking'} · ${booking.customerName}`}
        title={givingBack ? 'Give money back' : 'Take a payment'}
        onBack={onClose}
      />

      <div ref={fields} className="grid grid-cols-12 gap-4">
        <div className="col-span-12 xl:col-span-7">
          <Card>
            <p className="mb-3 text-sm font-semibold text-gray-900">This booking</p>
            <div className="flex flex-col gap-4">
              <Group label="What for">
                <div className="flex flex-wrap gap-1.5">
                  {offerableKinds.map((option) => {
                    const left = suggestedFor(booking, option);

                    return (
                      <Choice
                        key={option}
                        label={paymentKindLabel[option]}
                        caption={left > 0 ? `${cash(left)} left` : null}
                        chosen={kind === option}
                        disabled={busy}
                        onChoose={() => {
                          setKind(option);
                          reset();
                        }}
                      />
                    );
                  })}
                </div>
              </Group>

              {kind === 'Deposit' && booking.deposit.state !== 'NotTaken' && (
                <Notice tone="info" title="A deposit is already recorded">
                  This booking's deposit is {humanizeEnum(booking.deposit.state).toLowerCase()}.
                  Taking a second one is unusual — check the receipts before you do.
                </Notice>
              )}

              <MoneyLines>
                <MoneyLine label="Invoice total" value={cash(booking.invoiceTotal)} />
                <MoneyLine label="Paid so far" value={cash(booking.paidAmount)} />
                <MoneyLine
                  label="Balance"
                  value={cash(booking.balanceDue)}
                  tone={booking.balanceDue > 0 ? 'warning' : undefined}
                  strong
                />
              </MoneyLines>

              <p className="text-default mb-0 text-[length:var(--scale-list-meta)]">
                {givingBack
                  ? suggested > 0
                    ? `${cash(suggested)} was paid beyond what the booking owes. Type less to give back part of it.`
                    : 'Nothing has been paid beyond what the booking owes.'
                  : suggested > 0
                    ? `${cash(suggested)} is outstanding for this. Type less for part of it.`
                    : 'Nothing is outstanding for this, so type what the customer is paying.'}
              </p>
            </div>
          </Card>
        </div>

        <div className="col-span-12 xl:col-span-5">
          <Card>
            <p className="mb-3 text-sm font-semibold text-gray-900">
              {givingBack ? 'Out of which account' : 'Payment'}
            </p>
            <div className="flex flex-col gap-3">
              {accounts.isError ? (
                <Notice tone="danger" title="The accounts could not be read." />
              ) : accounts.isSuccess && parts.length === 0 ? (
                <Notice tone="warning" title="No account can take money at this branch.">
                  Add one in Cash and bank.
                </Notice>
              ) : (
                <SplitBoxes
                  parts={parts}
                  onChange={change}
                  direction={givingBack ? 'outOf' : 'into'}
                  busy={busy}
                />
              )}

              <QuickAmounts
                options={
                  suggested > 0 ? [{ label: `All ${cash(suggested)}`, amount: suggested }] : []
                }
                busy={busy}
                onPick={(amount) => change((current) => fillOne(current, amount))}
              />

              <Field label="Note · optional">
                <textarea
                  rows={2}
                  value={notes}
                  disabled={busy}
                  onChange={(event) => setNotes(event.target.value)}
                  className={textAreaClass}
                />
              </Field>

              {partly ? (
                <Notice tone="warning" title="Part of it went through">
                  {partly}
                </Notice>
              ) : null}
              {tried && problem ? (
                <p className="text-default mb-0 text-[length:var(--scale-list-meta)]">{problem}</p>
              ) : null}
              {used > 0 ? (
                <p className="text-default mb-0 text-[length:var(--scale-list-meta)]">
                  {splitWords(parts, methodWord, cash, givingBack ? 'from' : 'into')}.
                </p>
              ) : null}

              <Button
                variant="primary"
                data-enter-key-commit
                shortcut="save"
                disabled={busy}
                onClick={record}
                className="h-12 w-full justify-center text-[15px]"
              >
                {busy ? 'Taking…' : takePartsLabel(total, used, currency, kind)}
              </Button>
              <Button onClick={onClose} disabled={busy} className="w-full justify-center">
                Back to the booking
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
